import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { Button, Card, Input } from '../components/common';
import { resetPassword } from '../services/api';
import MouseBubble from '../components/common/MouseBubble';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
  ];

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const validateForm = () => {
    const errors: string[] = [];
    if (password !== confirmPassword) {
      errors.push('Passwords do not match');
    }
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters');
    }
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors([]);

    if (!token) return;
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-dark-dark dark:bg-white flex items-center justify-center transition-colors duration-300 py-12 px-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-500 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
            Password Reset Successful!
          </h1>
          <p className="text-gray-400 dark:text-gray-500 mb-6 transition-colors duration-300">
            Your password has been changed. You can now sign in with your new password.
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/login')}
            className="w-full"
          >
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-dark dark:bg-white flex items-center justify-center transition-colors duration-300 py-12 px-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white dark:text-dark-dark mb-2 transition-colors duration-300">
            Reset Password
          </h1>
          <p className="text-gray-400 dark:text-gray-500 transition-colors duration-300">
            Enter your new password
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0" />
            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {validationErrors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            {validationErrors.map((err, index) => (
              <p key={index} className="text-red-700 dark:text-red-400 text-sm">{err}</p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="relative">
            <Input
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              leftIcon={<Lock className="w-5 h-5" />}
              placeholder="Enter new password"
              required
              disabled={!token}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {password.length > 0 && (
            <div className="bg-dark-light dark:bg-gray-50 rounded-lg p-3 space-y-1">
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">Password requirements:</p>
              {passwordRequirements.map((req, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <CheckCircle className={`w-4 h-4 ${req.met ? 'text-green-500' : 'text-gray-400'}`} />
                  <span className={req.met ? 'text-green-500' : 'text-gray-400'}>
                    {req.text}
                  </span>
                </div>
              ))}
            </div>
          )}

          <Input
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            leftIcon={<Lock className="w-5 h-5" />}
            placeholder="Confirm new password"
            required
            disabled={!token}
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            disabled={isLoading || !token}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>

        <p className="mt-6 text-center text-gray-400 dark:text-gray-500 transition-colors duration-300">
          Remember your password?{' '}
          <Link to="/login" className="text-accent dark:text-primary hover:underline font-medium">
            Sign in
          </Link>
        </p>
      </Card>
      <MouseBubble />
    </div>
  );
};

export default ResetPasswordPage;

